import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { MessageTypes } from '../../base/package/types';
import { AttentionIconName } from '../ui-icons/ui-attention-icon';
import styles from './ui-message.css?inline';

const CName = 'ui-message';

/**
 * @prop {string} class - CSS class(es) to apply to the component. Determines the status icon to be rendered.
 * @prop {boolean} icon - To toggle type icon tied to MessageTypes. Hidden in small variation.
 * @prop {boolean} close - To toggle close icon visibility. Hidden in small variation.
 */
@customElement(CName)
export class UIMessage extends LitElement {
  static readonly styles = unsafeCSS(styles);
  @property({ type: String }) class = '';
  @property({ type: Boolean }) icon = false;
  @property({ type: Boolean }) close = false;
  @property({ type: Boolean }) shouldRemoveTopPadding = false;

  render() {
    const contentClasses = classMap({
      content: true,
      'content--no-top-padding': this.shouldRemoveTopPadding,
    });

    return html`
      <div class="message ${this.class}">
        ${this._renderStatusIcon()}
        <div class=${contentClasses}>
          <slot></slot>
          ${this._renderAction()}
        </div>
        ${this._renderCloseIcon()}
      </div>
    `;
  }

  private _renderStatusIcon() {
    if (this.icon && !this.class.includes('small')) {
      const messageTypeToIconName = {
        [MessageTypes.Success]: AttentionIconName.SUCCESS,
        [MessageTypes.Error]: AttentionIconName.ERROR,
        [MessageTypes.Warning]: AttentionIconName.WARNING,
        [MessageTypes.Info]: AttentionIconName.INFORMATION,
      };

      for (const [messageType, iconName] of Object.entries(messageTypeToIconName)) {
        if (this.class.includes(messageType)) {
          return html`<ui-attention-icon class="size-s" .name=${iconName}></ui-attention-icon>`;
        }
      }
    }

    return nothing;
  }

  private _renderCloseIcon() {
    return this.close && !this.class.includes('small')
      ? html`
          <div class="close">
            <slot name="close"> </slot>
          </div>
        `
      : nothing;
  }

  private _renderAction() {
    if (this.class.includes('small')) {
      return nothing;
    }

    return html`
      <div class="action">
        <slot name="action"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIMessage;
  }
}
